import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { CheckCircle, CreditCard, Shield, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentCheckout = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState({});
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' or 'paypal'

  const content = {
    fr: {
      title: 'Choisissez votre service',
      subtitle: 'Sélectionnez le plan qui correspond à vos besoins',
      checkout: 'Passer à la commande',
      processing: 'Traitement...',
      yourInfo: 'Vos informations',
      name: 'Nom complet',
      email: 'Email',
      phone: 'Téléphone',
      payWith: 'Payer maintenant',
      secure: 'Paiement 100% sécurisé',
      paymentMethod: 'Méthode de paiement',
      stripe: 'Carte bancaire (Stripe)',
      paypal: 'PayPal',
      included: 'Inclus dans ce pack',
      popular: 'Populaire',
      selectService: 'Veuillez sélectionner un service',
      fillForm: 'Veuillez remplir tous les champs'
    },
    en: {
      title: 'Choose your service',
      subtitle: 'Select the plan that fits your needs',
      checkout: 'Proceed to checkout',
      processing: 'Processing...',
      yourInfo: 'Your information',
      name: 'Full name',
      email: 'Email',
      phone: 'Phone',
      payWith: 'Pay now',
      secure: '100% secure payment',
      paymentMethod: 'Payment method',
      stripe: 'Credit card (Stripe)',
      paypal: 'PayPal',
      included: 'Included in this pack',
      popular: 'Popular',
      selectService: 'Please select a service',
      fillForm: 'Please fill all fields'
    }
  };

  const t = content[language];

  useEffect(() => {
    fetchServices();
    
    // Get service from URL params
    const serviceParam = searchParams.get('service');
    if (serviceParam) {
      setSelectedService(serviceParam);
    }
  }, [searchParams]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/services/public`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Erreur lors du chargement des services');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSelect = (servicePlan) => {
    setSelectedService(servicePlan);
    // Scroll to form
    document.getElementById('checkout-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCheckout = async (e) => {
    e.preventDefault();

    if (!selectedService) {
      toast.error(t.selectService);
      return;
    }

    if (!formData.name || !formData.email) {
      toast.error(t.fillForm);
      return;
    }

    setProcessing(true);

    try {
      if (paymentMethod === 'stripe') {
        // Create Stripe checkout session
        const response = await axios.post(`${API}/payment/stripe/create-checkout`, {
          service_plan: selectedService,
          user_id: `temp-${Date.now()}`,
          user_email: formData.email
        });

        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else if (paymentMethod === 'paypal') {
        // Create PayPal order
        const userId = `temp-${Date.now()}`;
        const response = await axios.post(`${API}/payment/paypal/create-order?service_plan=${selectedService}&user_id=${userId}&user_email=${encodeURIComponent(formData.email)}`);

        // Redirect to PayPal approval
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Erreur lors de la création du paiement');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">{t.title}</h1>
          <p className="text-lg text-slate-600">{t.subtitle}</p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {Object.entries(services).map(([key, service]) => {
            const isSelected = selectedService === key;
            const isPopular = key === 'pack_modeles';
            
            return (
              <Card
                key={key}
                className={`cursor-pointer transition-all hover:shadow-xl ${
                  isSelected
                    ? 'border-2 border-blue-900 shadow-xl'
                    : isPopular
                    ? 'border-2 border-blue-200'
                    : ''
                }`}
                onClick={() => handleServiceSelect(key)}
              >
                {isPopular && (
                  <div className="bg-blue-900 text-white text-center py-2 text-sm font-semibold">
                    {t.popular}
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl">{service.name[language]}</CardTitle>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-blue-900">€{service.price}</span>
                  </div>
                  <p className="text-slate-600 mt-4">{service.description[language]}</p>
                </CardHeader>
                <CardContent>
                  <p className="font-medium text-slate-700 mb-3">{t.included} :</p>
                  <ul className="space-y-2">
                    {service.features.slice(0, 6).map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full mt-6 ${
                      isSelected
                        ? 'bg-blue-900 hover:bg-blue-800'
                        : 'bg-slate-700 hover:bg-slate-800'
                    }`}
                    onClick={() => handleServiceSelect(key)}
                  >
                    {isSelected ? '✓ Sélectionné' : t.checkout}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Checkout Form */}
        {selectedService && (
          <Card id="checkout-form" className="max-w-2xl mx-auto shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-blue-900 to-blue-800 text-white">
              <CardTitle className="flex items-center gap-3">
                <CreditCard className="h-6 w-6" />
                {t.yourInfo}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleCheckout} className="space-y-6">
                <div>
                  <Label htmlFor="name">{t.name} *</Label>
                  <Input
                    id="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-2"
                    placeholder="Jean Dupont"
                  />
                </div>

                <div>
                  <Label htmlFor="email">{t.email} *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-2"
                    placeholder="jean.dupont@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">{t.phone}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-2"
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>

                <div>
                  <Label>{t.paymentMethod} *</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('stripe')}
                      className={`p-4 border-2 rounded-lg transition-all ${
                        paymentMethod === 'stripe'
                          ? 'border-blue-900 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <CreditCard className="h-5 w-5" />
                        <span className="font-medium">{t.stripe}</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('paypal')}
                      className={`p-4 border-2 rounded-lg transition-all ${
                        paymentMethod === 'paypal'
                          ? 'border-blue-900 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l1.12-7.106a1.29 1.29 0 0 1 1.277-1.094h2.19c4.298 0 7.664-1.747 8.647-6.797.03-.149.054-.294.077-.437a5.75 5.75 0 0 0-.086-.486z"/>
                        </svg>
                        <span className="font-medium">{t.paypal}</span>
                      </div>
                    </button>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-medium text-slate-900">Service sélectionné :</span>
                    <Badge className="bg-blue-900 text-white">
                      {services[selectedService]?.name[language]}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-2xl font-bold text-blue-900">
                    <span>Total :</span>
                    <span>€{services[selectedService]?.price}</span>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={processing}
                  className="w-full bg-blue-900 hover:bg-blue-800 h-12 text-lg"
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      {t.processing}
                    </>
                  ) : (
                    <>
                      <Shield className="h-5 w-5 mr-2" />
                      {t.payWith}
                      <ArrowRight className="h-5 w-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                  <Shield className="h-4 w-4 text-green-600" />
                  {t.secure}
                </p>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PaymentCheckout;
